import { Flask, ImageSquare } from '@phosphor-icons/react';
import {
  FLASH,
  LITE,
  TASK_MODELS,
  TEST_SENTENCES,
  checkSentence,
  type AiTask,
  type AiTaskStats,
} from '@shh/shared';
import { Busy, Button, ChoiceGroup, Heading, Surface, Text } from '@shh/ui';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { adminAiStats, adminAiTest, friendlyError, type AiTestResult } from '../data/api';
import { adminText } from '../i18n/admin';
import { AiBudgetSection } from './AdminAiBudget';
import { shrink } from '../lib/image';

/**
 * The AI tab of the admin area (1.27, Mike 26.10.): the measuring of the families' calls (no
 * content) and the test set – the built-in sentences checked automatically against both models,
 * and Mike's own photos side by side. Photos are only sent, never stored.
 */
const t = adminText.ai;
const MODELS = [LITE, FLASH] as const;
const label = (m: string) => (m === LITE ? 'Flash-Lite' : 'Flash');
const cents = (c: number) => `${c.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ct`;
const secs = (ms: number) =>
  `${(ms / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} s`;
const pct = (x: number) => `${Math.round(x * 100)} %`;

export function AdminAi() {
  return (
    <div className="flex flex-col gap-section">
      <AiBudgetSection />
      <Measuring />
      <SentenceTest />
      <PhotoTest />
    </div>
  );
}

function Measuring() {
  const [data, setData] = useState<{ week: AiTaskStats[]; month: AiTaskStats[] }>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    adminAiStats().then(setData, (e: unknown) => setError(friendlyError(e)));
  }, []);
  return (
    <section aria-labelledby="ai-measuring" className="flex flex-col gap-gap-md">
      <Heading level={2} size="lg" id="ai-measuring">
        {t.measuringTitle}
      </Heading>
      <Text tone="secondary">{t.measuringHint}</Text>
      {error && <p className="font-semibold text-feedback-danger">{error}</p>}
      {!data && !error && <Busy label={t.loading} />}
      {data && (
        <div className="overflow-x-auto rounded-card border border-border-subtle">
          <table className="w-full min-w-xl text-left text-sm tabular-nums">
            <thead className="text-xs text-text-secondary">
              <tr>
                <th className="p-inset-xs">{t.task}</th>
                <th className="p-inset-xs">{t.models}</th>
                <th className="p-inset-xs">{t.calls7}</th>
                <th className="p-inset-xs">{t.firstTry}</th>
                <th className="p-inset-xs">{t.failed}</th>
                <th className="p-inset-xs">{t.duration}</th>
                <th className="p-inset-xs">{t.cost30}</th>
              </tr>
            </thead>
            <tbody>
              {data.week.map((w) => {
                const m = data.month.find((x) => x.task === w.task)!;
                return (
                  <tr key={w.task} className="border-t border-border-subtle">
                    <th scope="row" className="p-inset-xs font-semibold">
                      {t.tasks[w.task]}
                    </th>
                    <td className="p-inset-xs">{TASK_MODELS[w.task].map(label).join(' → ')}</td>
                    <td className="p-inset-xs">{w.calls}</td>
                    <td className="p-inset-xs">{w.calls ? pct(w.firstTry) : '–'}</td>
                    <td className="p-inset-xs">{w.failed}</td>
                    <td className="p-inset-xs">
                      {w.calls ? `${secs(w.avgMs)} · 90 %: ${secs(w.p90Ms)}` : '–'}
                    </td>
                    <td className="p-inset-xs">{cents(m.costCents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type Row = { text: string; results: Partial<Record<string, AiTestResult & { right: boolean }>> };

function SentenceTest() {
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const stop = useRef(false);
  const run = async () => {
    setBusy(true);
    stop.current = false;
    const out: Row[] = TEST_SENTENCES.map((s) => ({ text: s.text, results: {} }));
    setRows([...out]);
    for (const [i, s] of TEST_SENTENCES.entries()) {
      for (const model of MODELS) {
        if (stop.current) break;
        try {
          const r = await adminAiTest({ task: 'sentence', model, text: s.text });
          out[i]!.results[model] = {
            ...r,
            right: r.ok && checkSentence(s, r.items as never),
          };
        } catch (e) {
          out[i]!.results[model] = {
            model,
            ok: false,
            error: friendlyError(e),
            items: [],
            ms: 0,
            attempts: 0,
            tokensIn: 0,
            tokensOut: 0,
            costCents: 0,
            right: false,
          };
        }
        setRows([...out]);
      }
    }
    setBusy(false);
  };
  const summary = (model: string) => {
    const done = rows.map((r) => r.results[model]).filter(Boolean) as (AiTestResult & {
      right: boolean;
    })[];
    if (!done.length) return null;
    const right = done.filter((d) => d.right).length;
    const ms = done.reduce((a, d) => a + d.ms, 0) / done.length;
    const cost = done.reduce((a, d) => a + d.costCents, 0) / done.length;
    return `${label(model)}: ${right}/${done.length} ${t.right} · Ø ${secs(ms)} · Ø ${cents(cost)}`;
  };
  return (
    <section aria-labelledby="ai-sentences" className="flex flex-col gap-gap-md">
      <Heading level={2} size="lg" id="ai-sentences">
        {t.sentencesTitle}
      </Heading>
      <Text tone="secondary">{t.sentencesHint(TEST_SENTENCES.length)}</Text>
      <div className="flex flex-wrap gap-gap-sm">
        <Button icon={Flask} isDisabled={busy} onPress={() => void run()}>
          {t.runSentences}
        </Button>
        {busy && (
          <Button variant="quiet" onPress={() => (stop.current = true)}>
            {t.stop}
          </Button>
        )}
      </div>
      {busy && <Busy variant="ai" label={t.running} />}
      {MODELS.map((m) => {
        const s = summary(m);
        return s ? (
          <Text key={m} weight="semibold">
            {s}
          </Text>
        ) : null;
      })}
      {rows.length > 0 && (
        <Surface className="flex flex-col divide-y divide-border-subtle">
          {rows.map((r) => (
            <div key={r.text} className="flex flex-col gap-gap-xs py-gap-sm">
              <span className="font-semibold">„{r.text}“</span>
              <div className="flex flex-wrap gap-gap-md text-sm">
                {MODELS.map((m) => {
                  const x = r.results[m];
                  return (
                    <span
                      key={m}
                      className={
                        x?.right
                          ? 'text-feedback-success'
                          : x
                            ? 'text-feedback-danger'
                            : 'text-text-muted'
                      }
                    >
                      {label(m)}:{' '}
                      {x
                        ? `${x.right ? '✓' : '✗'} ${secs(x.ms)} · ${cents(x.costCents)}${x.attempts > 1 ? ` · ${x.attempts} ${t.attempts}` : ''}${x.error ? ` · ${x.error}` : ''}`
                        : '…'}
                    </span>
                  );
                })}
              </div>
              {MODELS.some((m) => r.results[m] && !r.results[m]!.right) && (
                <details className="text-xs text-text-secondary">
                  <summary className="cursor-pointer">{t.whatCame}</summary>
                  {MODELS.map((m) => (
                    <pre key={m} className="overflow-x-auto whitespace-pre-wrap">
                      {label(m)}: {JSON.stringify(r.results[m]?.items ?? [], null, 1)}
                    </pre>
                  ))}
                </details>
              )}
            </div>
          ))}
        </Surface>
      )}
    </section>
  );
}

const PHOTO_TASKS: { value: AiTask; label: string }[] = [
  { value: 'photo', label: t.photoKinds.photo },
  { value: 'voucher', label: t.photoKinds.voucher },
  { value: 'recipePhoto', label: t.photoKinds.recipePhoto },
];

function PhotoTest() {
  const [task, setTask] = useState<AiTask>('photo');
  const [results, setResults] = useState<AiTestResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const file = useRef<HTMLInputElement>(null);
  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    setError(undefined);
    setResults([]);
    try {
      const img = await shrink(f);
      const out: AiTestResult[] = [];
      for (const model of MODELS) {
        out.push(
          await adminAiTest({ task, model, image: { mimeType: img.mimeType, data: img.data } }),
        );
        setResults([...out]);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-labelledby="ai-photos" className="flex flex-col gap-gap-md">
      <Heading level={2} size="lg" id="ai-photos">
        {t.photosTitle}
      </Heading>
      <Text tone="secondary">{t.photosHint}</Text>
      <ChoiceGroup label={t.photoKind} value={task} onChange={setTask} choices={PHOTO_TASKS} />
      <input ref={file} type="file" accept="image/*" hidden onChange={(e) => void pick(e)} />
      <Button
        icon={ImageSquare}
        className="self-start"
        isDisabled={busy}
        onPress={() => file.current?.click()}
      >
        {t.pickPhoto}
      </Button>
      {busy && <Busy variant="ai" label={t.running} />}
      {error && <p className="font-semibold text-feedback-danger">{error}</p>}
      {results.length > 0 && (
        <div className="grid gap-gap-md md:grid-cols-2">
          {results.map((r) => (
            <Surface key={r.model} className="flex min-w-0 flex-col gap-gap-xs">
              <span className="font-display text-lg font-black">{label(r.model)}</span>
              <span className="text-sm tabular-nums text-text-secondary">
                {r.ok ? t.found(r.items.length) : r.error} · {secs(r.ms)} · {cents(r.costCents)}
                {r.attempts > 1 ? ` · ${r.attempts} ${t.attempts}` : ''}
              </span>
              <ul className="flex flex-col gap-gap-xs text-sm">
                {r.items.map((i, n) => (
                  <li key={n}>{describe(i)}</li>
                ))}
              </ul>
            </Surface>
          ))}
        </div>
      )}
    </section>
  );
}

/** One found item in a line – enough to judge whether the photo was read right. */
function describe(i: Record<string, unknown>): string {
  const r = i.repeat as { freq?: string; days?: number[] } | undefined;
  return [
    i.kind,
    i.title ?? i.name ?? i.text ?? i.label,
    i.date,
    i.start ? `${String(i.start)}${i.end ? `–${String(i.end)}` : ''}` : undefined,
    Array.isArray(i.who) && i.who.length ? (i.who as string[]).join(', ') : undefined,
    r ? `↻ ${r.freq ?? 'weekly'} ${(r.days ?? []).join(',')}` : undefined,
    Array.isArray(i.days) ? `${(i.days as unknown[]).length} Tage` : undefined,
    Array.isArray(i.ingredients) ? `${(i.ingredients as unknown[]).length} Zutaten` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
}
